// shared-lib/cabinet-runs/vendorSizes.js
//
// The SIZE CATALOGS of vendor families (design 2026-10-05 §2): a family with
// `spec.sizes` builds only what its catalog resolves. One registry, so a
// second vendor is a second folder under `vendors/`. Pure.

const venus = require('./vendors/venus/resolveSku');

const VENDOR_SIZE_SETS = { venus };

/** The size catalog a family builds from, or null (generic families). */
function familySizes(family) {
  const key = family && family.spec && family.spec.sizes;
  return (key && VENDOR_SIZE_SETS[key]) || null;
}

/** One emitted cabinet → `{ sku, kind }` or `{ sku: null, reason }`. */
function resolveCabinetSku(family, obj, ctx = {}) {
  const sizes = familySizes(family);
  if (!sizes) return { sku: null, reason: 'family has no size catalog' };
  return sizes.resolveSku(obj, ctx);
}

/**
 * The KIND a family's catalog reads `obj` as ('wall', 'wall-bridge-24',
 * 'tall-pantry', …) — the same reading `resolveCabinetSku` prices it by,
 * answered even when the family makes no unit of that size, so a caller can
 * ask the catalog for the nearest one. Null for a family with no catalog.
 */
function cabinetKindOf(family, obj, ctx = {}) {
  const sizes = familySizes(family);
  return sizes ? sizes.kindOf(obj, ctx.profileId) : null;
}

/**
 * The fillers in `objects` wider than `family` sells as stock — with the
 * resolver's sentence for each (`{ object, reason }`). Empty for a generic
 * family. One rule for the drawn run's warning and the generator's gate
 * (2026-10-09).
 */
function tooWideFillers(family, objects, ctx = {}) {
  if (!familySizes(family)) return [];
  return (objects || [])
    .filter((o) => o?.category?.filler)
    .map((object) => ({ object, r: resolveCabinetSku(family, object, ctx) }))
    .filter(({ r }) => r.tooWide)
    .map(({ object, r }) => ({ object, reason: r.reason }));
}

/** A kind's catalog units, narrowest first (then shortest). */
// The kinds a vendor catalog lists that the estimate does NOT price by their
// part number: a roll-out tray is billed on the one `cabinet-rollout` row
// (`estimate/cabinetRollOutRows.js`), so a part-number price, bundle or
// import match for it would never be read (2026-10-25).
const NOT_PRICED_BY_SKU = new Set(['rollout-tray']);

/**
 * A family's catalog units the estimate prices by their part number — every
 * one but a roll-out tray. What the Price Book's vendor section lists, the
 * bundle editor offers as part numbers and the importer matches a part
 * number against. `[]` for a family with no size catalog.
 */
function skuPricedUnitsOf(family) {
  const sizes = familySizes(family);
  return sizes
    ? sizes.catalog.units.filter((u) => !NOT_PRICED_BY_SKU.has(u.kind))
    : [];
}

function unitsOf(family, kind) {
  const sizes = familySizes(family);
  if (!sizes) return [];
  return sizes.catalog.units
    .filter((u) => u.kind === kind)
    .sort((a, b) => a.w - b.w || a.h - b.h);
}

/**
 * The height (inches) a vendor family would build an upper of `kind` at,
 * `widthIn` wide, in a slot `heightIn` tall: the tallest it makes that fits.
 * null — it makes nothing that fits (the box is not built); undefined — the
 * family has no size catalog (build the slot as-is, today's behaviour).
 */
function snapUpperHeightIn(family, kind, widthIn, heightIn) {
  if (!familySizes(family)) return undefined;
  const w = Math.round(widthIn);
  const heights = unitsOf(family, kind)
    .filter((u) => u.w === w && u.h <= heightIn + 1e-6)
    .map((u) => u.h);
  return heights.length ? Math.max(...heights) : null;
}

/** An eighth of an inch: a width this close to a catalog width IS it. */
const WIDTH_TOL_IN = 0.125;

/**
 * The width (inches) a vendor family would build an upper of `kind` at in a
 * slot `widthIn` wide: the slot's own width when the family makes it, else
 * the widest it makes that fits. null — nothing fits; undefined — the family
 * has no size catalog.
 */
function fitUpperWidthIn(family, kind, widthIn) {
  if (!familySizes(family)) return undefined;
  const widths = [...new Set(unitsOf(family, kind).map((u) => u.w))];
  const exact = widths.find((w) => Math.abs(w - widthIn) <= WIDTH_TOL_IN);
  if (exact !== undefined) return exact;
  const fits = widths.filter((w) => w <= widthIn);
  return fits.length ? Math.max(...fits) : null;
}

/**
 * A family's vanity SINK units for `bowls` basins — a sink base or a
 * sink-drawer combination. Handed (L/R) units are left out: nothing that
 * builds a vanity picks a hand.
 */
function vanitySinkUnits(family, bowls = 1) {
  return [
    ...unitsOf(family, 'vanity-sink-base'),
    ...unitsOf(family, 'vanity-sink-drawer'),
  ].filter((u) => (bowls === 2 ? u.bowls === 2 : u.bowls !== 2) && !u.hand);
}

/**
 * The widths (inches, narrowest first) a family makes a vanity sink unit at
 * for `bowls` basins — Venus: 24"–60" for one bowl, only 60" for two. [] when
 * it makes none; null for a family with no size catalog.
 */
function vanitySinkWidthsIn(family, bowls = 1) {
  if (!familySizes(family)) return null;
  return [...new Set(vanitySinkUnits(family, bowls).map((u) => u.w))].sort(
    (a, b) => a - b
  );
}

/**
 * The body a family builds a vanity at, whatever its width: `{ heightIn,
 * depthIn }` — the family's vanity height and its sink units' depth (Venus:
 * 34.5" x 21"). null when it makes no vanity sink unit or has no catalog.
 */
function vanityBodyOf(family) {
  if (!familySizes(family)) return null;
  const units = [...vanitySinkUnits(family, 1), ...vanitySinkUnits(family, 2)];
  if (!units.length) return null;
  return {
    heightIn:
      family.spec.bodyHeights?.vanity ?? Math.max(...units.map((u) => u.h)),
    depthIn: Math.max(...units.map((u) => u.d)),
  };
}

/**
 * The vanity a vendor family would build for a generated vanity about
 * `widthIn` wide: the widest of its vanity SINK units (sink base or sink-drawer
 * combination) that fits — the 60" double for two basins — at the family's
 * vanity height and the unit's depth. Handed (L/R) units are left out: the
 * generator does not pick a hand. null — nothing fits; undefined — the family
 * has no size catalog.
 *
 * ONE unit, so it is the answer only where the vanity is one box: a vanity
 * placed on its own (`create_bathroom_vanity`). A vanity a LAYOUT planned is
 * built as a run over its whole width instead (`build_vanity`, 2026-10-21).
 */
function snapVanityToFamily(family, { widthIn, sinkCount = 1 }) {
  if (!familySizes(family)) return undefined;
  const units = vanitySinkUnits(family, sinkCount === 2 ? 2 : 1).filter(
    (u) => u.w <= widthIn + 1e-6
  );
  if (!units.length) return null;
  const best = units.reduce((a, b) => (b.w > a.w ? b : a));
  const heightIn = family.spec.bodyHeights?.vanity ?? best.h;
  return { widthIn: best.w, heightIn, depthIn: best.d };
}

const sameHex = (a, b) =>
  typeof a === 'string' &&
  typeof b === 'string' &&
  a.toLowerCase() === b.toLowerCase();

/**
 * The vendor colour code of a cabinet's finish — looked up WITHIN its door
 * design's colours, because two series sell near-identical whites (Houston
 * Frost, Slim Frost) at different prices. Null when the design is not sold in
 * that colour or the family has no codes.
 */
function colourCodeFor(family, obj) {
  const offering = family && family.offering;
  if (!offering || !Array.isArray(offering.paint) || !obj) return null;
  // A texture wins over paint (`cabinet-finish`), and a vendor that prices by
  // colour code sells none: a textured cabinet has no code.
  if (obj.texture && obj.texture.textureId) return null;
  // Which design the cabinet IS, read the way the panel reads it — a recorded
  // design yields to the drawn style when the two disagree. Required lazily:
  // familyOffering requires this module (VENDOR_SIZE_SETS).
  const { designForLook, offeringOf } = require('./familyOffering');
  const familyOffer = offeringOf(family);
  // A filler or panel has no door: it is drawn flat whatever the run's door,
  // so its drawn style says nothing about the series. Its colour is the run's,
  // read through the design the run records (2026-10-09).
  const doorless = Boolean(obj.category?.filler) || obj.type === 'blank-panel';
  const recorded =
    doorless &&
    (familyOffer.doorDesigns || []).find((d) => d.id === obj.doorDesign);
  const design = recorded || designForLook(familyOffer, obj);
  const ids = (design && design.finishes && design.finishes.paint) || [];
  const entry = offering.paint.find(
    (p) => ids.includes(p.id) && sameHex(p.hex, obj.paintColor)
  );
  return (entry && entry.code) || null;
}

/**
 * The drawers of the part a vendor family would sell for `obj` — read the way
 * the estimate prices it (`resolveCabinetSku`), so the drawing and the price
 * cannot name different parts: `{ count, graduated }` (graduated — a short top
 * drawer over equal ones), or null for a generic family, a part with no
 * drawer stack, or a box the family makes no part for.
 */
function drawerStackFor(family, obj, ctx = {}) {
  const sizes = familySizes(family);
  if (!sizes) return null;
  const { sku } = sizes.resolveSku(obj, ctx);
  const unit = sku && sizes.catalog.units.find((u) => u.sku === sku);
  if (!unit || !(unit.drawers > 0)) return null;
  return { count: unit.drawers, graduated: unit.graduated === true };
}

/**
 * The refrigerator end panel a vendor family sells — its catalog unit
 * `{ sku, w, h, d }` (inches; `w` is the panel's thickness along the wall) —
 * or null: a generic family, or a vendor that sells none. The tallest when
 * there are several, since a panel is cut DOWN to its column.
 */
function fridgeEndPanelOf(family) {
  const panels = unitsOf(family, 'fridge-end-panel');
  return panels.length ? panels.reduce((a, b) => (b.h > a.h ? b : a)) : null;
}

module.exports = {
  tooWideFillers,
  VENDOR_SIZE_SETS,
  fridgeEndPanelOf,
  familySizes,
  resolveCabinetSku,
  cabinetKindOf,
  drawerStackFor,
  unitsOf,
  skuPricedUnitsOf,
  colourCodeFor,
  snapUpperHeightIn,
  fitUpperWidthIn,
  snapVanityToFamily,
  vanitySinkWidthsIn,
  vanityBodyOf,
};
